import { useTranslation } from 'react-i18next';
import type { BroadcastPurpose } from '@/api/broadcasts/broadcastsApi.types';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

interface BroadcastPurposeSelectorProps {
  purpose: BroadcastPurpose;
  onChange: (purpose: BroadcastPurpose) => void;
  audienceCount: number;
  consentedCount: number | null;
}

interface PurposeOptionProps {
  label: string;
  description: string;
  isActive: boolean;
  onSelect: () => void;
}

function PurposeOption({ label, description, isActive, onSelect }: PurposeOptionProps) {
  return (
    <button
      type="button"
      aria-pressed={isActive}
      onClick={onSelect}
      className={cn(
        'flex-1 min-w-[12rem] text-left rounded-xl border px-3.5 py-2.5 transition-colors',
        isActive
          ? 'border-zooprimary bg-zoovpalelavender'
          : 'border-zooborder bg-zoowhite hover:border-zooprimary'
      )}
    >
      <span className="block text-sm font-semibold text-zooindigo">{label}</span>
      <span className="block text-xs text-zoomuted mt-0.5">{description}</span>
    </button>
  );
}

export function BroadcastPurposeSelector({
  purpose,
  onChange,
  audienceCount,
  consentedCount,
}: BroadcastPurposeSelectorProps) {
  const { t } = useTranslation('broadcasts');
  return (
    <div className="space-y-2">
      <Label>{t('compose.purposeLabel')}</Label>
      <div className="flex flex-wrap gap-2">
        <PurposeOption
          label={t('compose.purposeService')}
          description={t('compose.purposeServiceHint')}
          isActive={purpose === 'service'}
          onSelect={function selectService() {
            onChange('service');
          }}
        />
        <PurposeOption
          label={t('compose.purposeMarketing')}
          description={t('compose.purposeMarketingHint')}
          isActive={purpose === 'marketing'}
          onSelect={function selectMarketing() {
            onChange('marketing');
          }}
        />
      </div>
      {purpose === 'marketing' && consentedCount !== null ? (
        <p className="text-xs text-zooprimary font-semibold">
          {t('compose.marketingConsentedCount', {
            consented: consentedCount,
            total: audienceCount,
          })}
        </p>
      ) : null}
    </div>
  );
}
